import axios from "axios";

// Entferne die absolute URL, da wir den Proxy verwenden
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Legacy Frame Interface
export interface IFrame {
    _id: string;
    washpark: string;
    camera: string;
    url: string;
    ip: string;
    base_path: string;
    filename: string;
    hasAnnotation: boolean;
    index: number;
}

// Erweitertes Frame Interface mit den neuen Feldern
export interface IFrameExtended extends IFrame {
    cameraId: string;
    accountId: string;
    accountName: string;
    importBatch?: string;
    originalPath?: string;
    createdAt: Date;
    updatedAt: Date;
}

// Frame-Statistik Interface
export interface IFrameStats {
    cameraId: string;
    frames: {
        hasAnnotation: boolean;
        count: number;
    }[];
    totalFrames: number;
}

// Account-Statistik Interface
export interface IAccountStats {
    account: {
        totalFrames: number;
        annotatedFrames: number;
        cameraCount: number;
        percentComplete: number;
    };
    cameras: {
        cameraId: string;
        cameraName: string;
        washpark: string;
        totalFrames: number;
        annotatedFrames: number;
        percentComplete: number;
    }[];
}

export interface IClip {
    _id: string;
    washpark: string;
    camera: string;
    url: string;
    ip: string;
    base_path: string;
    filename: string;
    priority: boolean;
    reason: string;
}

export interface IPaginatedResponse<T> {
    data: T[];
    total: number;
    page: number;
    pageSize: number;
}

export interface ITrainingFilters {
    washpark?: string;
    camera?: string;
    hasAnnotation?: boolean;
    priority?: boolean;
    page?: number;
    pageSize?: number;
}

export interface ITrainingFiltersByAccountAndCamera {
    accountId?: string;
    cameraIds?: string | string[];
    hasAnnotation?: boolean;
}

export class TrainingService {
    private static client = axios.create({
        // baseURL: API_BASE_URL,  // Diese Zeile entfernen
        headers: {
            'Content-Type': 'application/json'
        }
    });

    private static getAuthHeader() {
        // Hole den Token aus dem localStorage mit dem korrekten Namen
        const token = localStorage.getItem('token'); // Änderung von 'session_token' zu 'token'
        console.log('Current token:', token); // Debug-Log
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    /**
     * Frames abrufen mit optionaler Filterung (Legacy)
     */
    static async getFrames(filters?: ITrainingFilters): Promise<IPaginatedResponse<IFrame>> {
        try {
            const headers = this.getAuthHeader();
            console.log('Request headers:', headers); // Debug-Log
            const response = await this.client.get<IPaginatedResponse<IFrame>>("/api/training/frames", { 
                params: filters,
                headers
            });
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden der Frames:", error);
            throw error;
        }
    }

    /**
     * Frames nach Account und Kamera-IDs abrufen (Neues Datenmodell)
     */
    static async getFramesByAccountAndCameras(filters: ITrainingFiltersByAccountAndCamera): Promise<IFrameExtended[]> {
        try {
            const { accountId, cameraIds, hasAnnotation } = filters;
            
            // Wenn cameraIds ein Array ist, konvertieren wir es zu einem Komma-getrennten String
            const cameraIdsParam = Array.isArray(cameraIds) ? cameraIds.join(',') : cameraIds;
            
            const response = await this.client.get<IFrameExtended[]>("/api/training/frames/by-account-cameras", {
                params: {
                    accountId,
                    cameraIds: cameraIdsParam,
                    hasAnnotation
                },
                headers: this.getAuthHeader()
            });
            
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden der Frames nach Account und Kameras:", error);
            throw error;
        }
    }

    /**
     * Frame-Statistiken nach Kamera-IDs abrufen
     */
    static async getFrameStatsByCameras(cameraIds: string[]): Promise<IFrameStats[]> {
        try {
            const response = await this.client.get<IFrameStats[]>("/api/training/frames/stats/by-cameras", {
                params: {
                    cameraIds: cameraIds.join(',')
                },
                headers: this.getAuthHeader()
            });
            
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden der Frame-Statistiken nach Kameras:", error);
            throw error;
        }
    }

    /**
     * Trainingsstatistiken nach Account abrufen
     */
    static async getTrainingStatsByAccount(accountId: string): Promise<IAccountStats> {
        try {
            const response = await this.client.get<IAccountStats>(`/api/training/stats/accounts/${accountId}`, {
                headers: this.getAuthHeader()
            });
            
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden der Trainingsstatistiken nach Account:", error);
            throw error;
        }
    }

    /**
     * Clips abrufen mit optionaler Filterung
     */
    static async getClips(filters?: ITrainingFilters): Promise<IPaginatedResponse<IClip>> {
        try {
            const response = await this.client.get<IPaginatedResponse<IClip>>("/api/training/clips", { 
                params: filters,
                headers: this.getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden der Clips:", error);
            throw error;
        }
    }

    /**
     * Frame-Annotation aktualisieren
     */
    static async updateFrameAnnotation(frameId: string, hasAnnotation: boolean): Promise<IFrame> {
        try {
            const response = await this.client.patch<IFrame>(`/api/training/frames/${frameId}/annotation`, {
                hasAnnotation
            }, {
                headers: this.getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error("Fehler beim Aktualisieren der Frame-Annotation:", error);
            throw error;
        }
    }

    /**
     * Clip-Priorität und Grund aktualisieren
     */
    static async updateClipPriority(clipId: string, priority: boolean, reason?: string): Promise<IClip> {
        try {
            const response = await this.client.patch<IClip>(`/api/training/clips/${clipId}/priority`, {
                priority,
                reason
            }, {
                headers: this.getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error("Fehler beim Aktualisieren der Clip-Priorität:", error);
            throw error;
        }
    }

    /**
     * Statistiken über Trainingsdaten abrufen
     */
    static async getTrainingStats() {
        try {
            const response = await this.client.get("/api/training/stats", {
                headers: this.getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden der Trainingsstatistiken:", error);
            throw error;
        }
    }

    /**
     * Frames nach Washpark und Kamera gruppiert abrufen
     */
    static async getFramesByWashparkAndCamera(): Promise<{ [washpark: string]: { [camera: string]: IFrame[] } }> {
        try {
            const response = await this.client.get("/api/training/frames/grouped", {
                headers: this.getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden der gruppierten Frames:", error);
            throw error;
        }
    }

    /**
     * Clips nach Washpark und Kamera gruppiert abrufen
     */
    static async getClipsByWashparkAndCamera(): Promise<{ [washpark: string]: { [camera: string]: IClip[] } }> {
        try {
            const response = await this.client.get("/api/training/clips/grouped", {
                headers: this.getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden der gruppierten Clips:", error);
            throw error;
        }
    }

    /**
     * Frame löschen
     */
    static async deleteFrame(frameId: string): Promise<void> {
        try {
            await this.client.delete(`/api/training/frames/${frameId}`, {
                headers: this.getAuthHeader()
            });
        } catch (error) {
            console.error("Fehler beim Löschen des Frames:", error);
            throw error;
        }
    }

    /**
     * Clip löschen
     */
    static async deleteClip(clipId: string): Promise<void> {
        try {
            await this.client.delete(`/api/training/clips/${clipId}`, {
                headers: this.getAuthHeader()
            });
        } catch (error) {
            console.error("Fehler beim Löschen des Clips:", error);
            throw error;
        }
    }

    /**
     * Get training status for specific cameras
     * This is a more efficient way to get training data in the future
     */
    static async getTrainingStatusByCameraIds(cameraIds: string[]): Promise<{ [cameraId: string]: { totalFrames: number, annotatedFrames: number } }> {
        try {
            const response = await this.client.post("/api/training/status-by-cameras", {
                cameraIds
            }, {
                headers: this.getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error("Fehler beim Laden des Kamera-Trainingsstatus:", error);
            throw error;
        }
    }
} 